import { TaskUrgency } from '@mimir/common/async/taskPriority';
import { makePriority, PrioritySemaphore } from '@mimir/common/async/prioritySemaphore';
import { ExponentialBackoff } from '@mimir/common/async/resilience';
import type { DynamoItemType} from '@mimir/common/dto/itemTypes';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { FileSetProgress, FileSetListener } from '@mimir/common/upload/uploadProgression';

import type { ItemRestoring, RestoreMediaModel} from '@mimir/common/dto/restoreMediaDto';
import { RestoreTier } from '@mimir/common/dto/restoreMediaRequestDto';
import type { ItemArtefact} from '@mimir/common/dto/transferRequestDtos';
import type { BasicUploadManager } from '@mimir/common/upload/BasicUploadManager';
import type { FileApi, FilePath } from '@mimir/common/upload/fileApi';
import { UploadState } from '@mimir/common/upload/upload';
import {
  itemTypeFromFilename,
  itemTypeFromMediaType,
  mediaTypeFromFilename,
} from '@mimir/common/util/mediaContentType';
import { invariant } from '@mimir/common/util/invariant';
import { exceptionEssence, makeError, exceptionToString, exceptionSummary } from '@mimir/common/util/exception';
import {
  copyFile,
  deleteFile,
  getLocalFileSize,
  hardLinkOrCopy,
  isDirAvailableLocally,
  isFileAvailableLocally,
  recursiveMkdirSync
} from '@mimir/common/util/fs';
import { wasHighresDeleted } from '@mimir/common/util/item';
import { isMediaOnline } from '@mimir/common/item/media';
import type { CancelToken } from 'axios';
import Axios from 'axios';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import type { ArtifactReference } from '@mimir/primitive/artifactReference';
import { isProxyOnlyArtifactReference, isSegmentedArtifactReference } from '@mimir/primitive/artifactReference';
import type {
  SyncItemInfo,
  ItemLocalMediaStates,
  LocalMediaState,
  NleClipInfo,
  NleClipInfoMap,
  NleMimirItemInfo,
} from '../fileSync/premiereTypes';
import type { SequenceExportInfo, SequenceInfo } from '@mimir/primitive/nle/sequence';
import { syncItemFieldList } from '../fileSync/premiereTypes';
import type { OngoingRestoreStatus } from '../model/ongoingDownload';
import type { Subclip } from '../model/subclip';
import { api_getItems } from '../api/getItemsApi';
import { api_createItem } from '../util/api';
import { constructExportedSequenceFileName } from '../util/fileNames';
import { UploadItem, type UploadContext } from './UploadItem';
import { runTask } from '@mimir/common/async/taskScope';
import type { TransientErrorCookie, TransientErrorHandler } from '@mimir/common/async/taskContext';
import type { DownloadIdentity, DownloadProspect } from '../fileSync/downloadIdentity';
import { planDownload } from '../fileSync/downloadPlanning';
import type { NleProjectItemReference, NleSequenceReference } from '@mimir/primitive/nle/reference';
import { proxyArtifactSupported } from '../fileSync/clipInfo';
import { deriveArtifactReferenceFromItem } from '../fileSync/resolveArtifactReference';
import { defaultAnalyticConfig, type ArchiveConfig, type UploadConfig } from '@mimir/common/dto/tenantModel';
import { getRouterLink } from '../util/basicRoute';
import { ItemState } from '@mimir/common/dto/itemState';
import { isPremiereMimirItemInfoPlaceholder } from '../util/premiere';
import type { Logger } from '@mimir/common/types/logger';
import { getBasename, getExtname } from '@mimir/common/platformAbstraction/pathComposition';
import type { ProjectItemUpdate } from '@mimir/primitive/nle/update';
import cloneDeep from 'lodash/cloneDeep';
import type { AdobePanelContext, ApplicationContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { NotificationOps } from '../fileSync/notificationOps';
import type { BackendClient } from '@mimir/common/util/backendClient';
import type { ItemWithSourceMedia } from './premiereItemTypes';
import type { LocalStateOps } from '../fileSync/localStateOps';
import type { ExportOps } from '../fileSync/exportOps';
import type { ProjectOps } from '../fileSync/projectOps';
import type { UploadOps } from '../fileSync/uploadOps';
import type { DownloadAndRelinkOps } from '../fileSync/downloadAndRelinkOps';
import { waitForAll } from '@mimir/common/async/async';
import type { ColdStorageRestoreOps } from '../coldStorage/coldStorageRestoreOps';
import type { LimitedNodeApi } from '@mimir/common/nodejs/limitedNodeApi/nodeApi';
import { AsyncOperationState } from '@mimir/common/dto/asyncOperationPrimitives';

export function getNonMimirClipsToUploadWith(clipInfos: NleClipInfoMap, uploadAllPremiereClips: boolean, sequenceExportInfo: SequenceExportInfo | undefined) {
  const nonMimirClipsMap: NleClipInfoMap = {};
  if (!sequenceExportInfo || isEmpty(sequenceExportInfo.tracks)) {
    // no tracks, no further checks needed
    return [];
  }

  if (uploadAllPremiereClips) {
    for (const clipInfo of Object.values(clipInfos)) {
      if (clipInfo.mimirItem && !isPremiereMimirItemInfoPlaceholder(clipInfo.mimirItem)) {
        // Already a Mimir item - skip
        continue;
      }

      if (clipInfo.projectPath !== sequenceExportInfo.projectPath) {
        // Not a clip in the same project as the sequence - skip
        continue;
      }

      if (!clipInfo.mediaPath) {
        // No physical file to upload - skip
        continue;
      }

      nonMimirClipsMap[clipInfo.mediaPath] = clipInfo;
    }
  } else {
    for (const track of sequenceExportInfo.tracks) {
      if (isEmpty(track.clips)) {
        // no clips in the track
        continue;

      }
      for (const clip of track.clips) {
        const clipInfo = clipInfos[clip.key!];
        if (!clipInfo || (clipInfo?.mimirItem && !isPremiereMimirItemInfoPlaceholder(clipInfo.mimirItem)) || isEmpty(clip.mediaPath) || isEmpty(clip.key)) {
          // already in Mimir, or clip does not have a file to upload. could be something like Bars and Tone,
          // Transparent Video etc. just skip it.
          continue;
        }

        // keyed with mediaPath, so that we eliminate duplicate files to upload
        nonMimirClipsMap[clip.mediaPath!] = clipInfo;
      }
    }
  }

  return Object.values(nonMimirClipsMap);
}

export function getTotalHighresSize(clipInfos: NleClipInfo[]) {
  return clipInfos.reduce((total: number, cur: NleClipInfo) =>
    total + (cur?.mimirItemLocalMediaStates?.highresState?.fileSize || 0), 0);
}

/*
  A clip whose item is segmented but which carries no artifact reference has lost its
  Mimir.Artifact metadata, so the segment range it was imported with is unknown. Premiere
  drops private project metadata when a clip is dragged into another project's sequence,
  and the panel can only restore the item ID from the file name. Bin to Bin works well.
*/
export const isClipBroken = (clipInfo: NleClipInfo) => {
  if (clipInfo.artifactRef !== null || !clipInfo.mimirItemId) {
    return false;
  }

  const item = clipInfo.mimirItem;
  if (!item || isPremiereMimirItemInfoPlaceholder(item)) {
    return false;
  }

  return isSegmentedArtifactReference(deriveArtifactReferenceFromItem(item));
};

// This is used to prevent proxy download from the Premiere sync page when proxy is not available.
// Checks if transcodingState is complete, which should cover for most items. But it seems some
// items do not have this property at all, e.g. Kelda generated segmented proxy. So we also check
// item.media for those cases.
export const proxyNotAvailableYet = (clipInfo: NleClipInfo) => clipInfo.mimirItemId && clipInfo.mimirItem?.transcodingState !== AsyncOperationState.COMPLETE && !clipInfo.mimirItem?.media;

export function mediaNeedsRelink(clipInfo: NleClipInfo, isProxy: boolean) {
  // case for aegraphic files
  if (clipInfo?.preferMediaPathInProject) {
    return false;
  }

  if (!clipInfo?.mimirItemId) {
    return false;
  }

  if (isClipBroken(clipInfo)) {
    return false;
  }

  if (!clipInfo?.mimirItemLocalMediaStates) {
    return false;
  }
  const localMediaState = clipInfo.mimirItemLocalMediaStates[isProxy ? 'proxyState' : 'highresState'];
  if (!localMediaState || !localMediaState.isAvailableLocally) {
    return false;
  }

  // it is available locally
  const pathInProject = clipInfo[isProxy ? 'proxyPath' : 'mediaPath'];

  const pathCanWork = (aLocalPath: string, fileExists: boolean) => !isEmpty(aLocalPath) && aLocalPath === pathInProject && fileExists;

  const { localPath, isAvailableLocally } = localMediaState;
  // Even if path is set correctly, we still need to execute the relinking process if the clip state is offline.
  if (pathCanWork(localPath!, isAvailableLocally) && !clipInfo.isOffline) {
    return false;
  }

  return true;
}

function getPreferredLocalPath(localMediaState?: LocalMediaState) {
  if (localMediaState?.isAvailableLocally) {
    return localMediaState?.localPath;
  }

  return '';
}

interface RelinkParams {
  clipInfo: NleClipInfo,
  filePath?: string;
  proxyFilePath?: string;
}

/**
 * Returns true when relinking would replace the media of an online clip with a
 * file of a different container type (different file extension).
 *
 * A legitimate relink never changes the media container of a clip that is
 * currently online: it repoints the clip at the same essence in a different
 * location. A container change on an online clip means the `Mimir.Item.Id`
 * stamped on the clip belongs to a different item — corrupted metadata seen in
 * the wild (issue #21326), where acting on the wrong ID rewired clips to
 * unrelated media and destroyed customer projects.
 */
export function relinkWouldChangeMediaTypeOfOnlineClip(
  clipInfo: NleClipInfo,
  currentPathInProject: string,
  targetLocalPath: string,
  pathSep: string,
) {
  if (clipInfo.isOffline || isEmpty(currentPathInProject)) {
    // Nothing usable is being replaced; relinking cannot make it worse.
    return false;
  }

  return getExtname(currentPathInProject, pathSep).toLowerCase() !== getExtname(targetLocalPath, pathSep).toLowerCase();
}

function getRelinkParams(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  clipInfo: NleClipInfo,
  isProxyEnabled: boolean,
) {
  if (!clipInfo.mimirItemId) {
    return;
  }

  const relinkParams: RelinkParams = {
    clipInfo,
  };

  relinkParams.filePath = !isProxyEnabled && mediaNeedsRelink(clipInfo, false) ? getPreferredLocalPath(clipInfo.mimirItemLocalMediaStates!.highresState)! : undefined;
  relinkParams.proxyFilePath = isProxyEnabled && mediaNeedsRelink(clipInfo, true) ? getPreferredLocalPath(clipInfo.mimirItemLocalMediaStates!.proxyState)! : undefined;

  const pathSep = services.applicationContext.pathSep;
  if (relinkParams.filePath !== undefined && relinkWouldChangeMediaTypeOfOnlineClip(clipInfo, clipInfo.mediaPath, relinkParams.filePath, pathSep)) {
    services.logger.error(`Refusing to relink online clip "${clipInfo.name}" from "${clipInfo.mediaPath}" to "${relinkParams.filePath}": the target has a different media container type. The Mimir.Item.Id (${clipInfo.mimirItemId}) stamped on this clip likely belongs to a different item.`);
    relinkParams.filePath = undefined;
  }
  if (relinkParams.proxyFilePath !== undefined && relinkWouldChangeMediaTypeOfOnlineClip(clipInfo, clipInfo.proxyPath, relinkParams.proxyFilePath, pathSep)) {
    services.logger.error(`Refusing to relink proxy of online clip "${clipInfo.name}" from "${clipInfo.proxyPath}" to "${relinkParams.proxyFilePath}": the target has a different media container type. The Mimir.Item.Id (${clipInfo.mimirItemId}) stamped on this clip likely belongs to a different item.`);
    relinkParams.proxyFilePath = undefined;
  }

  if (!relinkParams.filePath && !relinkParams.proxyFilePath) {
    return;
  }

  return relinkParams;
}

export async function relinkAllClipsWith(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
  },
  clipInfosArray: NleClipInfo[],
  isProxyEnabled: boolean,
  premiereState: { relinkingAllClips: boolean } | null,
) {
  try {
    if (premiereState) {
      premiereState.relinkingAllClips = true;
    }
    const relinkParamsList : RelinkParams[] = [];
    for (const clipInfo of clipInfosArray) {
      const relinkParams = getRelinkParams(services, clipInfo, isProxyEnabled);
      if (!relinkParams) {
        continue;
      }
      relinkParamsList.push(relinkParams);

    }

    for (const relinkParams of relinkParamsList) {
      services.notificationOps.notifyStartRelinking(relinkParams.clipInfo);
    }

    const updateProjectItemJobs: { clipInfo: NleClipInfo, update: ProjectItemUpdate }[]  = [];
    for (const relinkParams of relinkParamsList) {
      const { filePath, proxyFilePath, clipInfo } = relinkParams;

      services.notificationOps.notifyStartRelinking(clipInfo);

      const newClipInfo = cloneDeep(clipInfo);
      const update : ProjectItemUpdate = { meta: { } };
      // Just having filePath/proxyFilePath set from getRelinkParams is enough to indicate
      // that we need to relink, even if the path is the same as before, because it may be
      // the case that the clip state is offline despite pointing to an available file.
      if (proxyFilePath !== undefined) {
        update.proxyMediaPath = proxyFilePath;
        newClipInfo.proxyPath = proxyFilePath;
      }
      if (filePath !== undefined) {
        update.hiresMediaPath = filePath;
        newClipInfo.mediaPath = filePath;
      }
      if ((update.proxyMediaPath !== undefined) || (update.hiresMediaPath !== undefined)) {
        updateProjectItemJobs.push({ clipInfo: newClipInfo, update });
      }
    }

    await services.applicationContext.nle.updateProjectItems(updateProjectItemJobs.map(job => ({ itemRef: job.clipInfo.itemRef, update: job.update })));
    for (const updateProjectItemJob of updateProjectItemJobs) {
      // Important to mark it as online here, because this in-memory state is not refreshed
      // from Premiere right away.
      updateProjectItemJob.clipInfo.isOffline = false;
      updateProjectItemJob.clipInfo.isRelinking = false;
      services.notificationOps.notifyPremiereClipInfoUpdated(updateProjectItemJob.clipInfo);
    }
  } finally {
    if (premiereState) {
      premiereState.relinkingAllClips = false;
    }
  }
}

function relinkClipWith(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
  },
  clipInfo: NleClipInfo,
  isProxyEnabled: boolean,
) {
  return relinkAllClipsWith(services, [clipInfo], isProxyEnabled, null);
}

function isMimirArtifact(clipInfo: NleClipInfo) {
  return clipInfo.artifactRef != null;
}

export const highresNeedsToBeDownloaded = (clipInfo: NleClipInfo) => {
  return (clipInfo.artifactRef !== null) &&
    !isProxyOnlyArtifactReference(clipInfo.artifactRef) &&
    !clipInfo.preferMediaPathInProject &&
    !clipInfo.mimirItemLocalMediaStates.highresState!.isAvailableLocally &&
    !clipInfo.mimirItemLocalMediaStates.highresState!.isBeingDownloaded;
};

export const highresDownloadSelectable = (clipInfo: NleClipInfo) => {
  return highresNeedsToBeDownloaded(clipInfo) &&
    !clipInfo.mimirItemLocalMediaStates.highresState!.isStateRefreshing;
};

export const highresNeedsToBeRestored = (clipInfo: NleClipInfo) => {
  if (clipInfo.mimirItem) {
    return !isMediaOnline(clipInfo.mimirItem);
  }
  return false;
};

export function itemNeedsToBeRestored(clipInfo: NleClipInfo) {
  return highresDownloadSelectable(clipInfo) && highresNeedsToBeRestored(clipInfo);
}

export const clipIsBeingUploaded = (ongoingUploads: UploadItem[], clipInfo: NleClipInfo) => {
  return getOngoingUploadForClipWith(ongoingUploads, clipInfo, false) !== undefined;
}

export const highresNeedsToBeUploaded = (ongoingUploads: UploadItem[], clipInfo: NleClipInfo) => {
  return (!isMimirArtifact(clipInfo) || isPremiereMimirItemInfoPlaceholder(clipInfo.mimirItem)) &&
    clipInfo.mimirItemLocalMediaStates.highresState!.isAvailableLocally &&
    !clipIsBeingUploaded(ongoingUploads, clipInfo);
};

export function getOngoingUploadForClipWith(ongoingUploads: UploadItem[], clipInfo: NleClipInfo, includeCompleted: boolean) {
  const localPath = clipInfo?.mimirItemLocalMediaStates?.highresState?.localPath;
  return ongoingUploads.find((ongoingUpload: UploadItem) => {
    const filePath = (ongoingUpload as Partial<PremiereClipUploadItem>).filePath;
    return localPath === filePath && (includeCompleted || !includeCompleted && ongoingUpload.uploadState !== UploadState.COMPLETE);
  });
}

const convertWindowsPathToUnixLike = (filePath: string) => filePath.replace(/\\/g, '/');
export const convertUnixPathToWindowsLike = (filePath: string) => filePath.replace(/\//g, '\\');

const extensionsToSkipPathRewrite = [
  '.aegraphic'
];

// Skip path rewrite if uploaded file was .aegraphic, since rewriting it causes the graphic to disappear in Premiere. #2036
export function shouldSkipPathRewriteUnlessNecessary(filePath: string) {
  return extensionsToSkipPathRewrite.some(ext => filePath.toLowerCase().endsWith(ext));
}

// Skip path rewrite if uploaded file was .aegraphic, since rewriting it causes the graphic to disappear in Premiere. #2036
export function shouldSkipPathRewriteUnlessNecessaryWith(applicationContext: ApplicationContext, filePath: string) {
  const fileExtension = getExtname(filePath, applicationContext.pathSep);
  return extensionsToSkipPathRewrite.includes(fileExtension.toLowerCase());
}

/**
 * Copy the media to download path immediately after upload completes so that we can save
 * having to redownload it. If that path is read-only, just ignore this silently.
 */
export class PremiereClipUploadItem extends UploadItem {
  constructor(
    private services: {
      applicationContext: AdobePanelContext | ElectronContext,
      notificationOps: NotificationOps,
      logger: Logger,
      uploadContext: UploadContext,
      projectOps: ProjectOps,
      localStateOps: LocalStateOps,
    },
    private nleClipInfos: NleClipInfoMap,
    file: FileApi,
    uploadToS3Clone: boolean,
    uploadConfig: Partial<UploadConfig>,
    public clipInfo: NleClipInfo,
    private downloadPath: string,
  ) {
    super(services.uploadContext, file, uploadToS3Clone, uploadConfig, clipInfo.mimirItemId);
  }

  get relatedClipMediaPath() {
    return this.clipInfo.mediaPath;
  }

  get filePath() {
    return this.file.path;
  }

  public async onUploadCompleted() {
    await super.onUploadCompleted();
    this.services.logger.info(`Upload Premiere clip completed for item ${this.id}`);

    const downloadPath = this.downloadPath;
    let hiresMediaPath: string | undefined;
    if (!isEmpty(downloadPath) && !shouldSkipPathRewriteUnlessNecessaryWith(this.services.applicationContext, this.filePath!)) {
      try {
        const fileExtension = getExtname(this.filePath!, this.services.applicationContext.pathSep);
        const copyToPath =
          `${downloadPath}${this.services.applicationContext.pathSep}${this.id}${fileExtension}`;

        if (await isFileAvailableLocally(this.services.applicationContext.nodeApi, this.services.logger, copyToPath)) {
          this.services.logger.info(`Skipping copying since already exists: ${copyToPath}`);
        } else {
          this.services.logger.info(`Copying to: ${copyToPath}`);

          // TODO: does not throw error when writing to C:\Program Files\Something, but does not work
          // either, i.e file is not found at destination. same with validateWritability when setting the path. why is that?
          await hardLinkOrCopy(
            this.services.applicationContext.nodeApi,
            this.services.logger,
            this.filePath!,
            copyToPath,
            'overwrite',
            copyFile,
          );
        }

        hiresMediaPath = copyToPath;
      } catch (err) {
        this.services.logger.info(`Ignoring error copying to ${downloadPath}, possibly because of permissions. ${err}`);
      }
    }

    const clipInfos: NleClipInfo[] = Object.values(this.nleClipInfos);
    for (const clipInfo of clipInfos) {
      if (clipInfo.mimirItemId === this.id || clipInfo.mediaPath === this.filePath) {
        // Update the in-memory object here, because if it is done via processPremiereClipInfos,
        // it will get a ItemState.NEW, which will make refreshClipLocalMediaStates skip updating
        // path states because it skips placeholders.
        this.services.notificationOps.notifyPremiereClipInfoUpdated({
          ...clipInfo,
          mimirItem: uploadItemToMimirItemInfo(this),
        });

        // Note: we did not update the clipInfo object directly, but did it via
        // notifyPremiereClipInfoUpdated to add mimirItem. Since we preserve
        // same object references in global premiere.clipInfos via vueUpdateObjectContent,
        // this works now, i.e., the clipInfo in the following call to
        // updateClipStateAfterUpload has mimirItem set. There was a race condition
        // before when we were directly setting `premiere.clipInfos = clipInfos`
        // in setPremiereClipInfos. When uploading multiple clips, first one worked,
        // then the event from CEP made setPremiereClipInfos overwrite all objects
        // while this code was still holding the old reference to the map. This led to
        // subsequent clip uploads using the old clipInfo to call updateClipStateAfterUpload
        // below, and resulted in an inconsistent state in the sync page due to missing
        // mimirItem.
        await this.services.projectOps.updateClipStateAfterUpload(
          this.services,
          clipInfo,
          true,
          this.id,
          hiresMediaPath,
        );
      }
    }
  }
}

// the semaphore limit helps prevent stalling the UI when thousands of uploads get queued as a result of clicking "Upload all"
const uploadSemaphore = new PrioritySemaphore(5);

export interface ClipUploadProgressListener {
  onProgress(uploadItem: PremiereClipUploadItem, progress: FileSetProgress): void;
}

async function uploadClipFileWith(
  ongoingUploads: UploadItem[],
  uploadItem: PremiereClipUploadItem,
  injectIntoSearch: boolean,
  services: {
    uploadManager: BasicUploadManager,
    uploadOps: UploadOps,
    clipUploadProgressListener: ClipUploadProgressListener,
  },
) {
  const { uploadManager, uploadOps } = services;

  ongoingUploads.push(uploadItem);
  invariant(uploadItem.file != null, 'File should have been set');

  uploadOps.onPremiereClipUploadStart(uploadItem, injectIntoSearch);
  const priority = makePriority({ urgency: TaskUrgency.LongRunning });

  /*
    Retry forever with exponential backoff, matching web app upload behavior.
  */
  const resilience = new ExponentialBackoff({
    timeLimit: Infinity
  });

  const cts = Axios.CancelToken.source();

  // Track transient errors during retries, matching web app pattern (ItemUpload.vue)
  const transientErrors: string[] = [];

  const listener: FileSetListener = (progress: FileSetProgress) => {
    services.clipUploadProgressListener.onProgress(uploadItem, progress);
    uploadItem.op = { progress, cancel: cts.cancel };

    if (progress.percentage !== undefined) {
      uploadItem.uploadProgress = Math.floor(progress.percentage);
    }
  };

  /*
    TransientErrorHandler to report transient errors during retries.
  */
  const transientErrorHandler: TransientErrorHandler = {
    setTransientError: (error: any): TransientErrorCookie => {
      const msg = exceptionEssence(error);
      transientErrors.push(msg);
      return msg;
    },
    clearTransientError: (cookie: TransientErrorCookie) => {
      const msg = cookie as string;
      transientErrors.splice(transientErrors.indexOf(msg), 1);
    }
  };

  await runTask({ priority, resilience, cancelToken: cts.token, transientErrorHandler }, (tc) =>
    uploadSemaphore.limit(
      () =>
        uploadManager.uploadFileBatchForItem(
          tc,
          () => uploadItem.permanentId,
          [uploadItem],
          listener
        ),
      tc.priority,
      tc.cancelToken
    ));
}


/**
 * Tries to construct and return a download identity. Returns null if
 * the clip has no associated Mimir item ID (no mimirItem and no artifact with an itemId).
 */
export function tryMakeDownloadIdentityForClipInfo(artefact: ItemArtefact, clipInfo: NleClipInfo): DownloadIdentity | null {
  const artifactReference = clipInfo.artifactRef ?? (clipInfo.mimirItem === null ? null : deriveArtifactReferenceFromItem(clipInfo.mimirItem));
  const itemId = artifactReference?.itemId ?? clipInfo.mimirItem?.id ?? null;
  if (itemId === null) {
    return null;
  }
  return {
    itemId,
    artefact,
    artifactReference: artifactReference ?? { type: 'FlatFileReference', itemId },
  };
}


export function makeDownloadIdentityForItem(
  artefact: ItemArtefact,
  item: Pick<ItemWithSourceMedia, 'id' | 'media'>,
  artifactReference: ArtifactReference
): DownloadIdentity {
  return { itemId: item.id, artefact, artifactReference };
}


export async function getLocalMediaStatesForClip(
  localStateOps: LocalStateOps,
  clipInfo: NleClipInfo,
  forceRefresh: boolean,
): Promise<ItemLocalMediaStates | null> {
  const dlIdHighres = tryMakeDownloadIdentityForClipInfo('highres', clipInfo);
  if (dlIdHighres === null) {
    return null;
  }
  const dlIdProxy = tryMakeDownloadIdentityForClipInfo('proxy', clipInfo);
  if (dlIdProxy === null) {
    return null;
  }
  const item = clipInfo.mimirItem ?? null;
  if (isPremiereMimirItemInfoPlaceholder(item) || (!clipInfo.needsRefreshingLocalMediaStates && !forceRefresh)) {
    return null;
  }

  const localMediaStates = await getItemLocalMediaStatesWith(localStateOps, item, null, dlIdHighres, dlIdProxy);
  if (localMediaStates.highresState?.isAvailableLocally) {
    return localMediaStates;
  }

  if (!shouldSkipPathRewriteUnlessNecessary(clipInfo.mediaPath)) {
    return localMediaStates;
  }

  const highresPathState = await localStateOps.getPathLocalMediaState(clipInfo.mediaPath);
  if (!highresPathState.isAvailableLocally) {
    return localMediaStates;
  }

  return {
    ...localMediaStates,
    highresState: {
      ...localMediaStates.highresState,
      ...highresPathState,
    },
  };
}

export async function getItemLocalMediaStatesWith(
  localStateOps: LocalStateOps,
  item: ItemWithSourceMedia | null,
  subclip: Subclip | null,
  dlIdHighres: DownloadIdentity,
  dlIdProxy: DownloadIdentity
): Promise<ItemLocalMediaStates> {
  const [highresState, proxyState] = await Promise.all([
    localStateOps.getItemArtefactLocalMediaState({ ...dlIdHighres, artefact: 'highres' }, item, subclip),
    localStateOps.getItemArtefactLocalMediaState({ ...dlIdProxy, artefact: 'proxy' }, item, subclip)]);

  const result: ItemLocalMediaStates = {
    highresState,
    proxyState,
  };

  return result;
}

export const proxyNeedsToBeDownloaded = (clipInfo: NleClipInfo) => {
  return isMimirArtifact(clipInfo) &&
  proxyArtifactSupported(clipInfo) &&
    !clipInfo.mimirItemLocalMediaStates.proxyState!.isAvailableLocally &&
    !clipInfo.mimirItemLocalMediaStates.proxyState!.isBeingDownloaded;
};

export interface RelinkSyncOp {
  type: 'relink';
}
export interface UploadSyncOp {
  type: 'upload';
}
export type RelinkOrUploadSyncOp = RelinkSyncOp | UploadSyncOp;

export interface DownloadSyncOp {
  type: 'highres' | 'proxy';
  alsoDownloadHighres: boolean;
  isPremiereStandaloneMode: boolean;
  isProxyWorkflowEnabled: boolean;
}

export type SyncOperation = RelinkOrUploadSyncOp | DownloadSyncOp;

function isDownloadSyncOp(syncOp: SyncOperation): syncOp is DownloadSyncOp {
  return syncOp.type == 'highres' || syncOp.type == 'proxy';
}

/**
 * Even through the Mimir user interface may offer the user the ability to
 * choose to download a proxy artefact, such a choice is not always appropriate.
 *
 * A request for downloading proxy may also be augmented to request that the
 * highres should also be downloaded at the same time.
 *
 * Therefore we distinguish between a *requested* artefact in a DownloadSyncOp,
 * and a *resolved* list of item artefacts that we will actually attempt to
 * download.
 */
export function resolveArtefactTypes(syncOp: SyncOperation, clipInfo: NleClipInfo): ItemArtefact[] {
  // Only download operations can request proxy.
  if (!isDownloadSyncOp(syncOp)) {
    return ['highres'];
  }

  /* Only video items support proxy artefacts. */
  if (clipInfo.mimirItem?.itemType != ItemType.VIDEO) {
    return ['highres'];
  }

  if (syncOp.type == 'proxy') {
    if (syncOp.alsoDownloadHighres) {
      return ['proxy', 'highres'];
    }

    return ['proxy'];
  }

  return ['highres'];
}

export async function syncClipWithDownload(
  services: {
    notificationOps: NotificationOps,
    applicationContext: AdobePanelContext | ElectronContext,
    localStateOps: LocalStateOps,
    downloadAndRelinkOps: DownloadAndRelinkOps,
    logger: Logger,
  },
  clipInfo: NleClipInfo,
  operation: DownloadSyncOp,
  prefetched: Record<string, SyncItemInfo> | null,
  ongoingRestoreStatus: OngoingRestoreStatus | undefined,
) {
  if (!clipInfo.mimirItemLocalMediaStates || isClipBroken(clipInfo)) {
    return;
  }

  // Can't sync (download) if item not visible to user
  if (clipInfo.isNotVisibleToUser || clipInfo.isInRecycleBin) {
    return;
  }

  if (!isMimirArtifact(clipInfo)) {
    return;
  }

  /*
    It is important only to start refreshing the media state after we've
    determined that there is an operation we need to do. Since otherwise the
    the media cell will show an "access_time" icon.
  */
  const mediaState = mediaStateForSyncOp(operation, clipInfo);
  services.notificationOps.notifyStartRefreshingMediaState(mediaState!);
  const resolvedArtefacts = resolveArtefactTypes(operation, clipInfo);

  const dlOp = operation;
  const itemId = clipInfo.artifactRef?.itemId ?? clipInfo.mimirItem?.id ?? null;
  invariant(itemId, 'Expected clipInfo to resolve to itemId');
  const itemRef = clipInfo.itemRef;
  for (const artefact of resolvedArtefacts) {
    if (artefact === 'proxy' && proxyNotAvailableYet(clipInfo)) {
      // If proxy is wanted but not available yet, skip it.
      continue;
    }

    const needsToBeDownloaded = artefact == 'highres' ? highresNeedsToBeDownloaded(clipInfo) : proxyNeedsToBeDownloaded(clipInfo);
    if (!needsToBeDownloaded) {
      // No need to download but relink anyway to update media path.
      await relinkClipWith(services, clipInfo, artefact == 'proxy');
      continue;
    }

    if (artefact === 'highres' && clipInfo.mimirItem?.highresDeletion && wasHighresDeleted(clipInfo.mimirItem)) {
      // Skip highres download if deleted
      continue;
    }

    const downloadIdentity = tryMakeDownloadIdentityForClipInfo(artefact, clipInfo);
    if (downloadIdentity === null) {
      continue;
    }
    await doDownloadAndRelink(services, dlOp, downloadIdentity, artefact, prefetched, itemRef, ongoingRestoreStatus);
  }

  if (clipInfo.mimirItem !== undefined) {
    // refresh states to reflect in UI
    const dlIdHighres = tryMakeDownloadIdentityForClipInfo('highres', clipInfo);
    invariant(dlIdHighres !== null, 'Unexpected null dlIdHighres value since clipInfo.mimirItem is defined');
    const dlIdProxy = tryMakeDownloadIdentityForClipInfo('proxy', clipInfo);
    invariant(dlIdProxy !== null, 'Unexpected null dlIdProxy value since clipInfo.mimirItem is defined');
    const newLocalMediaStates = await getItemLocalMediaStatesWith(services.localStateOps, clipInfo.mimirItem, null, dlIdHighres, dlIdProxy);
    // Avoid passing the full stale clipInfo to notifyPremiereClipInfoUpdated: a projectChanged event
    // may have fired during the download and updated the store with fresh data (e.g. mediaPath).
    // Only patch mimirItemLocalMediaStates, which is the only field this function is responsible for.
    services.notificationOps.notifyPremiereClipLocalMediaStatesUpdated(clipInfo.key, newLocalMediaStates);
  }
  services.notificationOps.notifyStopRefreshingMediaState(mediaState!);
}

export async function syncClipWithRelink(
  services: {
    notificationOps: NotificationOps,
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  config: {
    isProxyMode: boolean,
  },
  clipInfo: NleClipInfo,
) {
  if (!clipInfo.mimirItemLocalMediaStates || isClipBroken(clipInfo)) {
    return;
  }

  await relinkClipWith(services, clipInfo, config.isProxyMode);
}

export async function syncClipWithUpload(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    uploadContext: UploadContext,
    projectOps: ProjectOps,
    notificationOps: NotificationOps,
    logger: Logger,
    uploadManager: BasicUploadManager,
    uploadOps: UploadOps,
    localStateOps: LocalStateOps,
  },
  config: {
    downloadPath: string,
  },
  injectIfUpload: boolean,
  nleClipInfos: NleClipInfoMap,
  ongoingUploads: UploadItem[],
  clipInfo: NleClipInfo,
  itemToUpload: PremiereClipUploadItem | undefined,
  toFolderId: string | undefined,
) {
  if (!clipInfo.mimirItemLocalMediaStates || isClipBroken(clipInfo)) {
    return;
  }

  // Can't sync (download) if item not visible to user
  if (clipInfo.isNotVisibleToUser || clipInfo.isInRecycleBin) {
    return;
  }

  if (!highresNeedsToBeUploaded(ongoingUploads, clipInfo)) {
    return;
  }
  if (getOngoingUploadForClipWith(ongoingUploads, clipInfo, false)) {
    // skip upload if the same file is being uploaded already
    return;
  }

  const clipUploadItem =
    itemToUpload ??
      createUploadItemForClip(
        clipInfo,
        nleClipInfos,
        toFolderId,
        ongoingUploads,
        config,
        services
      );

  // Set the mogrtParentItemId on the uploadItem if available. This is used
  // later from beforeUpload in order to create a relation with this child
  // item and the mogrt parent item.
  if (clipInfo.mimirMogrtParentItemId) {
    clipUploadItem.mogrtParentItemId = clipInfo.mimirMogrtParentItemId;
  }

  const noopProgressListener: ClipUploadProgressListener = {
    onProgress: () => {}
  };
  const uploadServices = {
    clipUploadProgressListener: noopProgressListener,
    ...services,
  };
  return await doUploadSyncClipWith(
    clipUploadItem,
    ongoingUploads,
    injectIfUpload,
    uploadServices,
  );
}

export async function doUploadSyncClipWith(
  uploadItem: PremiereClipUploadItem,
  ongoingUploads: UploadItem[],
  injectIntoSearch: boolean,
  services: {
    notificationOps: NotificationOps,
    logger: Logger,
    uploadManager: BasicUploadManager,
    uploadOps: UploadOps,
    clipUploadProgressListener: ClipUploadProgressListener,
  },
) {
  const clipInfo = uploadItem.clipInfo;
  const highresState = clipInfo.mimirItemLocalMediaStates.highresState;
  invariant(highresState, 'Expected highres state for upload');
  try {
    await uploadClipFileWith(
      ongoingUploads,
      uploadItem,
      injectIntoSearch,
      services,
    );
  } catch (error) {
    services.logger.info(`Error while uploading clips: ${error}`);
    if (!Axios.isCancel(error)) {
      services.notificationOps.displayBriefError(`Error while uploading clip: ${exceptionEssence(error)}`);
    }
    throw error;
  }
}

export function createUploadItemForClip(
  clipInfo: NleClipInfo,
  nleClipInfos: NleClipInfoMap,
  toFolderId: string | undefined,
  ongoingUploads: UploadItem[],
  config: {
    downloadPath: string,
  },
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    uploadContext: UploadContext,
    projectOps: ProjectOps,
    localStateOps: LocalStateOps,
  }
): PremiereClipUploadItem {
  const mediaState = mediaStateForSyncOp({ type: 'upload' }, clipInfo);
  invariant(mediaState, 'Expected at least one resolved artefact');
  const filePath = mediaState.localPath;
  invariant(filePath != null, 'Media state must have localPath to attempt upload');

  const fileName = getBasename(filePath, services.applicationContext.pathSep);
  const file: FilePath = {
    name: fileName,
    path: filePath,
    size: getLocalFileSize(services.applicationContext.nodeApi, filePath)
  };
  const uploadConfig: Partial<UploadConfig> = {
    ...clipInfo.analyticConfig,
    folderId: toFolderId
  };
  return new PremiereClipUploadItem(services, nleClipInfos, file, false, uploadConfig, clipInfo, config.downloadPath);
}


function mediaStateForSyncOp(
  op: SyncOperation,
  clipInfo: NleClipInfo,
) {
  const resolvedArtefacts = resolveArtefactTypes(op, clipInfo);
  const { proxyState, highresState } = clipInfo.mimirItemLocalMediaStates;
  const mediaState = resolvedArtefacts[0] == 'proxy' ? proxyState : highresState;
  invariant(mediaState, 'Expected at least one resolved artefact');
  return mediaState;
}

export function planSyncDownload(
  applicationContext: ApplicationContext,
  dlOp: DownloadSyncOp,
  downloadIdentity: DownloadIdentity,
): DownloadProspect {
  return planDownload({
    isProxyWorkflowEnabled: dlOp.isProxyWorkflowEnabled,
    isPremiereStandaloneMode: dlOp.isPremiereStandaloneMode,
    downloadIdentity,
  });
}


async function doDownloadAndRelink(
  services: {
    applicationContext: AdobePanelContext | ElectronContext;
    downloadAndRelinkOps: DownloadAndRelinkOps,
  },
  dlOp: DownloadSyncOp,
  downloadIdentity: DownloadIdentity,
  resolvedArtefact: ItemArtefact,
  prefetched: Record<string, SyncItemInfo> | null,
  itemRef: NleProjectItemReference | null,
  ongoingRestoreStatus: OngoingRestoreStatus | undefined,
) {
  const plan = planSyncDownload(services.applicationContext, dlOp, downloadIdentity);
  if (plan.blocker != null) {
    throw makeError(plan.blocker, 'DownloadBlocked');
  }

  if (itemRef) {
    plan.deliver = {
      type: 'DeliverPremiereItemUpdate',
      itemRef
    };
  }
  plan.dlId.artefact = resolvedArtefact;

  await services.downloadAndRelinkOps.downloadAndRelink(dlOp.isProxyWorkflowEnabled, dlOp.isPremiereStandaloneMode, plan, prefetched, ongoingRestoreStatus);
}

export async function restoreAndSyncClip(
  services: {
    logger: Logger,
    coldStorageRestoreOps: ColdStorageRestoreOps,
    notificationOps: NotificationOps,
    applicationContext: AdobePanelContext | ElectronContext,
    localStateOps: LocalStateOps,
    downloadAndRelinkOps: DownloadAndRelinkOps,
  },
  config: {
    isProxyMode: boolean,
    downloadPath: string,
    archiveConfig: ArchiveConfig | undefined,
    userDataSystemPath: string,
  },
  clipInfo: NleClipInfo,
  operation: DownloadSyncOp & { type: 'highres' },
  prefetched: Record<string, SyncItemInfo> | null,
) {
  if (!clipInfo.mimirItemId) {
    services.logger.info('Missing mimirItemId');
    return;
  }
  if (!config.archiveConfig) {
    services.logger.info('Missing archive config, cannot proceed restore before downloading');
    return;
  }
  const restoreMediaModel: RestoreMediaModel = {
    itemId: clipInfo.mimirItemId,
    tier: config.archiveConfig.defaultRestoreTier,
    retentionDays: config.archiveConfig.defaultRetentionDays,
  };

  let restoreMediaResult: ItemRestoring | null;
  const coldStorageRestoreResponse = await services.coldStorageRestoreOps.requestRestore(restoreMediaModel.itemId, restoreMediaModel.tier, restoreMediaModel.retentionDays);
  if (coldStorageRestoreResponse.objectOnline) {
    restoreMediaResult = {
      itemId: restoreMediaModel.itemId,
      // If the object is actually online, the restore tier does not matter so let's just
      // reuse the requested value
      tier: restoreMediaModel.tier,
    };
  } else if (coldStorageRestoreResponse.restoreStatus.restoreWasAlreadyInProgress) {
    // If the object was already being restored, the legacy code returns a null result,
    // so let's do the same
    restoreMediaResult = null;
  } else {
    restoreMediaResult = {
      itemId: restoreMediaModel.itemId,
      tier: coldStorageRestoreResponse.restoreStatus.effectiveRestoreTier,
    };
  }

  if (restoreMediaResult) {
    const ongoingRestoreStatus: OngoingRestoreStatus = {
      tier: restoreMediaResult.tier,
      retentionDays: restoreMediaModel.retentionDays,
      showUpgradeOption: restoreMediaModel.tier === RestoreTier.Expedited && restoreMediaResult.tier !== RestoreTier.Expedited,
      isUpgrading: false,
    };
    await syncClipWithDownload(
      services,
      clipInfo,
      operation,
      prefetched,
      ongoingRestoreStatus,
    );
  }
}

export async function syncClipsWithRelink(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
  },
  config: {
    isProxyMode: boolean,
  },
  clipInfosArray: NleClipInfo[],
  premiereState: { relinkingAllClips: boolean } | null,
) {
  return relinkAllClipsWith(services, clipInfosArray, config.isProxyMode, premiereState);
}

export async function syncClipsWithUpload(
  services: {
    notificationOps: NotificationOps,
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
    uploadContext: UploadContext,
    uploadManager: BasicUploadManager,
    projectOps: ProjectOps,
    uploadOps: UploadOps,
    localStateOps: LocalStateOps,
  },
  config: {
    isProxyMode: boolean,
    downloadPath: string,
    archiveConfig: ArchiveConfig | undefined,
    userDataSystemPath: string,
  },
  injectIfUpload: boolean,
  // All the clips
  nleClipInfos: NleClipInfoMap,
  ongoingUploads: UploadItem[],
  // The clips to process
  clipInfosArray: NleClipInfo[],
  itemsToUpload: PremiereClipUploadItem[] | undefined,
  toFolderId: string | undefined,
) {
  const promises: Promise<unknown>[] = [];
  for (const clipInfo of clipInfosArray) {
    const needsToBeRestored = itemNeedsToBeRestored(clipInfo);

    if (!needsToBeRestored) {
      const itemToUpload = itemsToUpload && itemsToUpload.find(item => item.relatedClipMediaPath === clipInfo.mediaPath);
      promises.push(
        syncClipWithUpload(
          services,
          config,
          injectIfUpload,
          nleClipInfos,
          ongoingUploads,
          clipInfo,
          itemToUpload,
          toFolderId,
        )
      );
    }
  }

  // Wait for all promises to be either rejected or resolved. Do not stop at the first rejection, because we don't want
  // one bad clip to stop the syncing of other possibly good clips.
  await waitForAll(promises);
}

export async function syncClipsWithDownload(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    backendClient: BackendClient,
    localStateOps: LocalStateOps,
    downloadAndRelinkOps: DownloadAndRelinkOps,
    logger: Logger,
    coldStorageRestoreOps: ColdStorageRestoreOps
  },
  config: {
    isProxyMode: boolean,
    downloadPath: string,
    archiveConfig: ArchiveConfig | undefined,
    userDataSystemPath: string,
  },
  // The clips to process
  clipInfosArray: NleClipInfo[],
  operation: DownloadSyncOp,
) {
  let prefetched: Record<string, SyncItemInfo> = {};
  const requestedProxy = operation.type == 'proxy';
  const itemIds = compact(clipInfosArray.map(clip => clip.mimirItemId));
  if (itemIds.length) {
    const items = await api_getItems(services.backendClient, itemIds, syncItemFieldList) as SyncItemInfo[];
    prefetched = keyBy(items, i => i.id);
  }

  const promises: Promise<unknown>[] = [];
  // Only create download notification, if the operation is a download/sync op
  const notifier = services.notificationOps.createNotifier('Downloading items', '', undefined);

  for (const clipInfo of clipInfosArray) {
    const needsToBeRestored = itemNeedsToBeRestored(clipInfo);

    if (needsToBeRestored) {
      const resolvedArtefacts = resolveArtefactTypes(operation, clipInfo);
      if (resolvedArtefacts.includes('highres')) {
        /*
          TODO: Pass in already resolved artefact instead of constructing dummy
          operation here.
        */
        const requestedHighresOp: DownloadSyncOp & { type: 'highres' } = { ...operation, type: 'highres' };
        promises.push(
          restoreAndSyncClip(
            services,
            config,
            clipInfo,
            requestedHighresOp,
            prefetched,
          )
        );
      }
    }

    if (!needsToBeRestored || requestedProxy) {
      promises.push(
        syncClipWithDownload(
          services,
          clipInfo,
          operation,
          prefetched,
          undefined,
        )
      );
    }
  }

  // Wait for all promises to be either rejected or resolved. Do not stop at the first rejection, because we don't want
  // one bad clip to stop the syncing of other possibly good clips.
  await waitForAll(promises);

  if (notifier) {
    notifier.setCalculating(false);
  }
}

export function uploadDependenciesWith(
  services: {
    notificationOps: NotificationOps,
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
    uploadContext: UploadContext,
    uploadManager: BasicUploadManager,
    projectOps: ProjectOps,
    uploadOps: UploadOps,
    localStateOps: LocalStateOps,
  },
  config: {
    isProxyMode: boolean,
    downloadPath: string,
    archiveConfig: ArchiveConfig | undefined,
    userDataSystemPath: string,
  },
  nleClipInfos: NleClipInfoMap,
  ongoingUploads: UploadItem[],
  uploadAllPremiereClips: boolean,
  sequenceExportInfo: SequenceExportInfo | undefined,
  folderId: string | undefined,
  itemsToUpload: PremiereClipUploadItem[] | undefined,
): {
  clipsToUpload: NleClipInfo[];
  uploadPromise: Promise<void> | undefined;
  operationConclusionToken: CancelToken | undefined;
} {
  const allNonMimirClips = getNonMimirClipsToUploadWith(nleClipInfos, uploadAllPremiereClips, sequenceExportInfo);
  if (allNonMimirClips.length === 0) {
    return { clipsToUpload: [], uploadPromise: undefined, operationConclusionToken: undefined };
  }

  const operationConclusion = Axios.CancelToken.source();
  const uploadPromise =
    syncClipsWithUpload(
      services,
      config,
      false,
      nleClipInfos,
      ongoingUploads,
      allNonMimirClips,
      itemsToUpload,
      folderId
    ).then(() => operationConclusion.cancel('concluded'));
  return { uploadPromise, clipsToUpload: allNonMimirClips, operationConclusionToken: operationConclusion.token };
}

export async function getSequenceExportInfoWith(
  services: {
    notificationOps: NotificationOps,
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
    exportOps: ExportOps,
  },
  nleClipInfos: NleClipInfoMap,
  sequenceRef: NleSequenceReference,
  presetPath: string | null,
  suppressErrorToast: boolean = false,
): Promise<SequenceInfo> {
  let seqInfo: SequenceInfo;
  try {
    seqInfo = await services.exportOps.getSequenceExportInfo(sequenceRef, presetPath ?? '');
  } catch (err) {
    services.logger.error(`getSequenceExportInfo: Error in retrieving sequence info: ${exceptionToString(err)}`);
    services.notificationOps.displayBriefError('Error in retrieving sequence: ' + exceptionSummary(err));
    throw err;
  }

  const exportResult = seqInfo.exportInfo;

  if (exportResult && exportResult.tracks && exportResult.tracks.length) {
    correctInPointOfImageClips(exportResult, nleClipInfos);
  }

  if (exportResult && exportResult.errorMessage && !suppressErrorToast) {
    services.notificationOps.displayBriefError(exportResult.errorMessage);
    throw new Error(exportResult.errorMessage);
  }

  return seqInfo;
}

/**
 * We have observed that in the clip info we get from Premiere the
 * "inPoint" and "outPoint" of image clips are set to large nonsensical
 * values, so we manually overwrite these.
 */
function correctInPointOfImageClips(sequenceInfo: SequenceExportInfo, clipInfos: NleClipInfoMap) {
  const clipItemType = (clipKey: string | undefined): DynamoItemType | null => {
    if (!clipKey) {
      return null;
    }
    const clipInfo = clipInfos[clipKey];
    const itemType = clipInfo?.mimirItem?.itemType;
    if (itemType && itemType !== 'folder') {
      /*
        TODO: We actually never expect itemType folder here. The need for this
        check is a quirk of the mimirItem type definition. Once we fix this type
        issue, we can remove the (itemType !== 'folder') check.
      */
      return itemType;
    } else if (clipInfo?.mimirItemId && clipInfo.mediaPath) {
      // If this is a mimir item we should normally get into the
      // above if case where we have looked up the item type already.
      // But if not (for example if the getItem call has failed)
      // we fall back to looking at the file extension.
      return itemTypeFromFilename(clipInfo.mediaPath);
    }
    return null;
  };

  for (const track of sequenceInfo.tracks) {
    for (const clip of track.clips) {
      if (clipItemType(clip.key) === ItemType.IMAGE) {
        clip.inPoint = 0;
        clip.outPoint = clip.duration ?? 0;
      }
    }
  }
}

/**
 * Updates item's existing sequence information with given info.
 * Called each time export preset is changed.
 * @param uploadItem Item object to be uploaded
 * @param sequenceInfo Export sequence information retrieved from premiere panel code
 */
export const assignSequenceParameters = (uploadItem: UploadItem, sequenceInfo: SequenceExportInfo) => {
  uploadItem.parentItemIds = sequenceInfo.parentItemIds;
  if (sequenceInfo.exportedFileExtension?.length > 0) {
    const exportedFileMediaType = mediaTypeFromFilename(`.${sequenceInfo.exportedFileExtension}`);
    uploadItem.mediaType = exportedFileMediaType;
    uploadItem.derivedItemType = itemTypeFromFilename(exportedFileMediaType);

    /*
      TODO: The property this.file.name is only the basename of the file, which
      means that any "/" or "\\" inside of this string is intended to be part of
      the basename, and not be a path separator.

      It is therefore technically incorrect to call
      constructExportedSequenceFileName() here which transitively performs
      another getBasename() call with a given path separator, which may differ
      from the original context where the basename was captured.

      We should instead call a variant of constructExportedSequenceFileName()
      that does not do any additional interpretation of path separators.
    */
    const pathSep = '/';
    uploadItem.file = {
      ...uploadItem.file,
      name: constructExportedSequenceFileName(uploadItem.file.name, pathSep, uploadItem.metadata, sequenceInfo.exportedFileExtension),
    };
  }

  if (!isEmpty(sequenceInfo.tracks)) {
    uploadItem.sequenceDetails = {
      projectType: 'prproj',
      tracks: sequenceInfo.tracks,
      sequenceInPointInMs: sequenceInfo.sequenceInPointInMs,
      sequenceOutPointInMs: sequenceInfo.sequenceOutPointInMs,
    };
  }
};

export async function makeDirWith(
  services: {
    logger: Logger,
    applicationContext: AdobePanelContext | ElectronContext,
  },
  dirPath: string,
) {
  if (await isDirAvailableLocally(services.applicationContext.nodeApi, services.logger, dirPath)) {
    return;
  }

  // On windows, makedir does not work for multi level dirs without having backslashes.
  if (services.applicationContext.isWindows) {
    dirPath = convertUnixPathToWindowsLike(dirPath);
  }

  services.logger.info(`recursiveMkdirSync: ${dirPath}`);
  recursiveMkdirSync(services.applicationContext.nodeApi, dirPath);
}

function getMimirDataPath(applicationContext: ApplicationContext, userDataSystemPath: string): string {
  return `${userDataSystemPath}${applicationContext.pathSep}mimir-premiere-panel`;
}

export function getDirPathInMimirDataPath(applicationContext: ApplicationContext, userDataSystemPath: string, dirName: string) {
  const mimirDataPath = getMimirDataPath(applicationContext, userDataSystemPath);
  return `${mimirDataPath}${applicationContext.pathSep}${dirName}`;
}

async function getDirInUserData(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  userDataSystemPath: string,
  dirName: string,
): Promise<string> {
  const mimirDataPath = getMimirDataPath(services.applicationContext, userDataSystemPath);
  const dirPath = getDirPathInMimirDataPath(services.applicationContext, userDataSystemPath, dirName);

  if (!await isDirAvailableLocally(services.applicationContext.nodeApi, services.logger, dirPath)) {
    services.logger.info(`Dir not found at ${dirPath}. Creating...`);
    await makeDirWith(services, mimirDataPath);
    await makeDirWith(services, dirPath);

    if (!await isDirAvailableLocally(services.applicationContext.nodeApi, services.logger, dirPath)) {
      const msg = `Dir could not be created at ${dirPath}`;
      services.logger.error(msg);
      throw new Error(msg);
    }
  }

  return dirPath;
}

export const TEMP_FOLDER_NAME = 'temp';

/**
 * We ask Premiere to get user data dir which we can use as scratch/temp space.
 * We don't use os.tmpdir() since that is seen to return '\tmp' on windows which cannot be given
 * to AME. Also, by using our own temp dir, we can control when to purge it.
 */
export function getTempDirInUserDataWith(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  userDataSystemPath: string,
): Promise<string> {
  return getDirInUserData(services, userDataSystemPath, TEMP_FOLDER_NAME);
}

export function readAsBrowserFile(applicationContext: AdobePanelContext | ElectronContext, filePath?: string): FilePath {
  if (!filePath) {
    throw new Error('no filePath');
  }

  const filename = getBasename(filePath, applicationContext.pathSep);

  if (applicationContext.isWindows) {
    filePath = convertWindowsPathToUnixLike(filePath);
  }

  return {
    name: filename,
    path: filePath,
    size: getLocalFileSize(applicationContext.nodeApi, filePath),
  };
}


/**
 * When deleteAfterwards is true (local render to temp dir) then we need to clean up on completion or error
 */
export class PremiereSequenceUploadItem extends UploadItem {
  public deleteAfterwards = false;

  constructor(private nodeApi: LimitedNodeApi, context: UploadContext, file: FileApi, uploadToS3Clone: boolean, config: Partial<UploadConfig> = {}, itemId?: string) {
    super(context, file, uploadToS3Clone, config, itemId);
  }

  public async onUploadCompleted() {
    await super.onUploadCompleted();
    this.cleanUp();
  }

  public async onUploadError() {
    await super.onUploadError();
    this.cleanUp();
  }

  private cleanUp() {
    if (this.deleteAfterwards) {
      deleteFile(this.nodeApi, this.file.path!);
    }
  }
}

export const createPlaceholderForClip = async (
  services: {
    applicationContext: ApplicationContext,
    notificationOps: NotificationOps,
    backendClient: BackendClient,
    projectOps: ProjectOps,
    localStateOps: LocalStateOps,
  },
  ongoingUploads: UploadItem[],
  clip: NleClipInfo,
  targetFolderId: string | undefined,
  taskUrgency: TaskUrgency,
  itemToUpload: PremiereClipUploadItem | undefined,
) => {
  const clipFilePath = clip.mimirItemLocalMediaStates.highresState?.localPath ?? clip.mediaPath;
  const clipFileName = getBasename(clipFilePath, services.applicationContext.pathSep);
  const priority = makePriority({ urgency: taskUrgency });
  const itemCreationResponse = itemToUpload ? await itemToUpload.beforeUpload(priority) : undefined;
  const placeholderResponse = itemCreationResponse || await api_createItem(
    services.backendClient,
    {
      title: clipFileName,
      filename: clipFileName,
      fileType: mediaTypeFromFilename(clipFileName),
      analyticConfig: defaultAnalyticConfig(),
      folderParents: targetFolderId ? [targetFolderId] : null,
      parentItemIds: undefined,
      sequenceDetails: undefined,
      metadata: undefined,
      isHidden: undefined,
      isP2Part: undefined,
      options: { priority },
    },
  );
  services.notificationOps.notifyPremiereClipInfoUpdated({
    ...clip,
    mimirItemId: placeholderResponse.id,
    mimirItem: {
      ...placeholderResponse,
      itemState: ItemState.NEW,
      proxy: null,
      transcodingState: undefined
    }
  });
  await services.projectOps.updateClipStateAfterUpload(services, clip, true, placeholderResponse.id, undefined);
};

const uploadItemToMimirItemInfo = (uploadItem: UploadItem): NleMimirItemInfo => {
  const itemType = itemTypeFromMediaType(uploadItem.mediaType);
  return {
    id: uploadItem.id,
    originalFileName: uploadItem.file.name,
    itemType,
    link: getRouterLink(itemType, { id: uploadItem.id }, { returnTo: 'premiere' }),
    createdBy: uploadItem.createdBy,
    itemState: ItemState.HIGHRES_AVAILABLE,
    proxy: null,
    transcodingState: undefined
  };
};
